import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { API_CONFIG } from '../../../core/config/api.config';

@Component({
  selector: 'app-trial-widget',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './trial-widget.component.html',
  styleUrl: './trial-widget.component.css'
})
export class TrialWidgetComponent implements OnInit, OnDestroy {
  visible = false;
  collapsed = false;
  days = 0;
  hours = 0;
  minutes = 0;
  seconds = 0;
  totalDays = 0;
  planName = '';
  companyName = '';
  expired = false;
  urgent = false;
  progress = 100; // percentage of trial remaining
  tick = false;   // toggles every second for blink animation

  // Drag state
  x = 0;
  y = 0;
  dragging = false;
  private dragOffsetX = 0;
  private dragOffsetY = 0;
  private hasMoved = false;

  private trialEndDate: Date | null = null;
  private trialStartDate: Date | null = null;
  private intervalId: ReturnType<typeof setInterval> | null = null;

  constructor(
    private http: HttpClient,
    private router: Router,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this.x = window.innerWidth - 300;
    this.y = window.innerHeight - 200;

    const companyId = localStorage.getItem('selectedCompanyId');
    if (!companyId) return;

    this.companyName = localStorage.getItem('selectedCompanyName') || '';
    this.planName = localStorage.getItem('selectedCompanyPlan') || '';

    const cachedStatus = localStorage.getItem('selectedCompanySubscriptionStatus');
    const cachedTrialEnd = localStorage.getItem('selectedCompanyTrialEndsAt');
    this.applyTrialStatus(cachedStatus, cachedTrialEnd, null);

    this.http.get<any>(`${API_CONFIG.partnersService}/companies/${companyId}`).subscribe({
      next: (company) => {
        this.companyName = company.name || this.companyName;
        this.planName = company.planName || company.planCode || this.planName;

        let trialEnd = company.trialEndsAt;
        if (!trialEnd && company.createdAt) {
          const trialDays = company.trialDays || 14;
          const created = new Date(company.createdAt);
          created.setDate(created.getDate() + trialDays);
          trialEnd = created.toISOString();
        }

        this.applyTrialStatus(company.subscriptionStatus, trialEnd, company.createdAt);
      },
      error: () => {},
    });
  }

  ngOnDestroy() {
    if (this.intervalId) clearInterval(this.intervalId);
  }

  private applyTrialStatus(status: string | null, trialEndsAt: string | null, createdAt: string | null) {
    // Only show widget for companies that are in trial mode
    // Companies with status 'active' (like Alez Auto Parts, Garage Express Pièces) skip
    if (!status || status === 'active') {
      this.visible = false;
      return;
    }

    if (!trialEndsAt) {
      this.visible = false;
      return;
    }

    this.trialEndDate = new Date(trialEndsAt);
    this.trialStartDate = createdAt ? new Date(createdAt) : null;
    this.updateCountdown();
    this.visible = true;

    // Update every second for live countdown
    if (!this.intervalId) {
      this.intervalId = setInterval(() => {
        this.tick = !this.tick;
        this.updateCountdown();
      }, 1_000);
    }
  }

  private updateCountdown() {
    if (!this.trialEndDate) return;
    const diff = this.trialEndDate.getTime() - Date.now();
    if (diff <= 0) {
      this.expired = true;
      this.urgent = true;
      this.days = this.hours = this.minutes = this.seconds = 0;
      this.progress = 0;
      return;
    }

    const totalSec = Math.floor(diff / 1000);
    const totalMin = Math.floor(totalSec / 60);
    const totalHrs = Math.floor(totalMin / 60);
    this.totalDays = Math.floor(totalHrs / 24);
    this.days = this.totalDays;
    this.hours = totalHrs % 24;
    this.minutes = totalMin % 60;
    this.seconds = totalSec % 60;
    this.urgent = this.totalDays <= 3;
    this.expired = false;

    // Progress bar: percentage of trial remaining
    if (this.trialStartDate) {
      const total = this.trialEndDate.getTime() - this.trialStartDate.getTime();
      const remaining = this.trialEndDate.getTime() - Date.now();
      this.progress = Math.max(0, Math.min(100, (remaining / total) * 100));
    } else {
      this.progress = Math.max(0, Math.min(100, (this.totalDays / 14) * 100));
    }
  }

  get progressColor(): string {
    if (this.progress > 50) return '#22c55e';
    if (this.progress > 25) return '#f59e0b';
    return '#ef4444';
  }

  toggle() {
    if (!this.hasMoved) {
      this.collapsed = !this.collapsed;
    }
  }

  goToBilling() {
    this.router.navigate(['/billing']);
  }

  // ── Drag ──

  onMouseDown(e: MouseEvent) {
    this.dragging = true;
    this.hasMoved = false;
    this.dragOffsetX = e.clientX - this.x;
    this.dragOffsetY = e.clientY - this.y;
    e.preventDefault();
  }

  onTouchStart(e: TouchEvent) {
    this.dragging = true;
    this.hasMoved = false;
    const t = e.touches[0];
    this.dragOffsetX = t.clientX - this.x;
    this.dragOffsetY = t.clientY - this.y;
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(e: MouseEvent) {
    if (!this.dragging) return;
    this.hasMoved = true;
    this.x = Math.max(0, Math.min(window.innerWidth - 60, e.clientX - this.dragOffsetX));
    this.y = Math.max(0, Math.min(window.innerHeight - 40, e.clientY - this.dragOffsetY));
  }

  @HostListener('document:mouseup')
  onMouseUp() { this.dragging = false; }

  @HostListener('document:touchmove', ['$event'])
  onTouchMove(e: TouchEvent) {
    if (!this.dragging) return;
    this.hasMoved = true;
    const t = e.touches[0];
    this.x = Math.max(0, Math.min(window.innerWidth - 60, t.clientX - this.dragOffsetX));
    this.y = Math.max(0, Math.min(window.innerHeight - 40, t.clientY - this.dragOffsetY));
  }

  @HostListener('document:touchend')
  onTouchEnd() { this.dragging = false; }
}
